import { createSlice, Draft, PayloadAction } from "@reduxjs/toolkit";
import { startOfMonth, subMonths } from "date-fns";
import moment from "moment";

import { stateDate } from "../../../../utils/formatDate";
import { createMultiOptions } from "../../../../utils/createOptions";
import { IReportsOptions } from "../../../../types/reportsInterfaces";

const userDataContracts =
  JSON.parse(localStorage.getItem("userData") as string)?.settings?.contracts ||
  [];

const initialState: any = {
  reports: null,
  reportsCount: null,
  limit: JSON.parse(localStorage.getItem("pageLimit") as string) || 10,
  page: 1,
  reportsInputValues: {
    fromDate: stateDate(startOfMonth(subMonths(new Date(), 1))),
    toDate: stateDate(new Date()),
    regions: [],
    contracts:
      userDataContracts.length === 1 ? [userDataContracts[0]?.contract.id] : [],
    projects: [],
    rateStatuses: [],
    reviewStatuses: [],
    deliveryStatuses: [],
    mediaStatus: null,
    inRange: null,
    supervisors: [],
  },
  formattedDateFrom: moment(
    stateDate(startOfMonth(subMonths(new Date(), 1)))
  ).format("DD/MM/YYYY"),
  formattedDateTo: moment(stateDate(new Date())).format("DD/MM/YYYY"),
  reportsSettings: JSON.parse(
    localStorage.getItem("selectedReportsTabs") as string
  ) || ["date", "chain", "address", "rate", "review", "contract"],
  adminReportsSettings: JSON.parse(
    localStorage.getItem("selectedAdminReportsTabs") as string
  ) || [
      "reportId",
      "date",
      "review",
      "delivery",
      "rate",
      "geo",
      "photo",
      "contract",
      "chain",
      "region",
      "address",
      "project",
      "sv",
      "vsv",
      "merchRoutes",
      "clientId",
    ],
  actualPurpose: null,
  actualModalTitle: null,
  isPending: false,
};

export const reportsSlice = createSlice({
  name: "reports",
  initialState,
  reducers: {
    reducerUserReports: (state: Draft<any>, action: PayloadAction<any>) => {
      state.reports = action.payload.data?.data || [];
      state.reportsCount = action.payload.data?.count || 0;
      state.limit = action.payload?.limit;
      state.page = action.payload?.page;
      state.checkboxes = action.payload.data?.data?.reduce(
        (acc: {[id: string]: boolean}, report: IReportsOptions) => {
          acc[report.reportId] = false;
          return acc;
        },
        {}
      );
    },
    reducerGetUserReportsFilters: (
      state: Draft<any>,
      action: PayloadAction<any>
    ) => {
      state.filters = action.payload.data;
      state.regionsOptions = createMultiOptions(action.payload.data[0]?.regions);
      state.areasOptions = createMultiOptions(action.payload.data[0]?.areas);

      state.chainsOptions = createMultiOptions(
        (action.payload.data[0]?.chains ?? [])
          .sort((a: {name: string}, b: {name: string}) => a.name.localeCompare(b.name))
      );

      state.contractsOptions = createMultiOptions(action.payload.data[0]?.contracts);
      state.projectOptions = createMultiOptions(action.payload.data[0]?.projects);
      state.svsOptions = createMultiOptions(action.payload.data[0]?.svs);
      state.merchsOptions = createMultiOptions(action.payload.data[0]?.merchs);
      state.vsvsOptions = createMultiOptions(action.payload.data[0]?.vsvs);
      state.reviewOptions = action.payload.data[0]?.reviewStatuses.map((el: { [key: string]: string }) => ({
        value: Object.keys(el).toString(),
        label: Object.values(el).toString()
      }));

      state.rateOptions = action.payload.data[0]?.rateStatuses.map((el: { [key: string]: string }) => ({
        value: Object.keys(el).toString(),
        label: Object.values(el).toString()
      }));
      state.responsibleCustomersOptions =
        action.payload.data[0]?.responsibleCustomers?.map((option: string) => ({
          value: option,
          label: option,
        }));
    },
    reducerGetUnloadReportsPhotoFilters: (
      state: Draft<any>,
      action: PayloadAction<any>
    ) => {
      state.photoFilters = action.payload.data;
      state.customServicesOptions = action.payload.data.customServices?.map(
        (option: string, index: number) => ({
          value: index,
          label: option,
        })
      );
      state.actionOptions = action.payload.data.actions?.map(
        (option: string, index: number) => ({
          value: index,
          label: option,
        })
      );
    },
    reducerUserReportsInputValues: (
      state: Draft<any>,
      action: PayloadAction<any>
    ) => {
      state.reportsInputValues = action.payload;
      state.reportContracts = action.payload?.contracts;
      state.formattedDateFrom = moment(action.payload.fromDate).format(
        "DD/MM/YYYY"
      );
      state.formattedDateTo = moment(action.payload.toDate).format(
        "DD/MM/YYYY"
      );
    },
    reducerUserReportsSelectValues: (
      state: Draft<any>,
      action: PayloadAction<any>
    ) => {
      state.reportsInputValues = { ...state.reportsInputValues, ...action.payload };
    },
    reducerReportsStatus: (state: Draft<any>, action: PayloadAction<any>) => {
      state.reportsStatus = action?.payload?.reportsStatus;
      state.reportsRequestPurpose = action?.payload?.reportsRequestPurpose;
    },
    reducerSelectedReports: (state: Draft<any>, action: PayloadAction<any>) => {
      state.selectedReportsId = action.payload;
    },
    reducerSelectedUpdateStatus: (
      state: Draft<any>,
      action: PayloadAction<any>
    ) => {
      state.updateStatus = action.payload;
    },
    reducerReportsSettings: (state: Draft<any>, action: PayloadAction<any>) => {
      state.reportsSettings = action.payload;
    },
    reducerAdminReportsSettings: (
      state: Draft<any>,
      action: PayloadAction<any>
    ) => {
      state.adminReportsSettings = action.payload;
    },
    reducerHeaderBtn: (state: Draft<any>, action: PayloadAction<any>) => {
      state.actualPurpose = action.payload.purpose;
      state.actualModalTitle = action.payload.title;
      state.openModal = action.payload.openModal;
    },
    reducerPending: (state: Draft<any>, action: PayloadAction<any>) => {
      state.isPending = action.payload;
    },
    reducerUserReportsClearInputValues: () => {
      return initialState;
    },
  },
});

export const {
  reducerGetUnloadReportsPhotoFilters,
  reducerUserReportsInputValues,
  reducerUserReportsSelectValues,
  reducerGetUserReportsFilters,
  reducerSelectedUpdateStatus,
  reducerAdminReportsSettings,
  reducerPending,
  reducerSelectedReports,
  reducerReportsSettings,
  reducerReportsStatus,
  reducerUserReports,
  reducerHeaderBtn,
  reducerUserReportsClearInputValues,
} = reportsSlice.actions;

export default reportsSlice.reducer;
